import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomerLogos from '@/components/CustomerLogos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { submitToCustomerIO } from '@/utils/apiUtils';
import { Send, MessageSquare, Shield, CheckCircle2 } from 'lucide-react';

const RequestDemo = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if ((window as any).gtag) {
      (window as any).gtag('event', 'form_submit_attempt', {
        form_name: 'Request Demo',
        form_location: '/request-demo'
      });
    }

    try {
      await submitToCustomerIO({
        email,
        name,
        company,
        message
      }, 'demo-request');

      if ((window as any).gtag) {
        (window as any).gtag('event', 'form_submit_success', {
          form_name: 'Request Demo',
          form_location: '/request-demo'
        });
      }

      toast({
        title: "Demo request submitted!",
        description: "We'll be in touch shortly."
      });

      setEmail('');
      setName('');
      setCompany('');
      setMessage('');
    } catch (error) {
      if ((window as any).gtag) {
        (window as any).gtag('event', 'form_submit_error', {
          form_name: 'Request Demo',
          form_location: '/request-demo',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
      toast({
        title: "Error submitting request",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Request Fordje Demo",
    "description": "Request a personalized demo of Fordje's regulatory data platform",
    "url": "https://www.fordje.com/request-demo",
    "mainEntity": {
      "@type": "Organization",
      "name": "Fordje",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Sales",
        "availableLanguage": "English"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Book a Demo — Fordje Regulatory Data Platform</title>
        <meta name="description" content="Book a personalized demo of Fordje. See how clean energy teams navigate codes, permitting, and interconnection across U.S. jurisdictions." />
        <meta name="keywords" content="Fordje demo, regulatory data demo, clean energy demo, solar permitting demo, building codes demo, request demo, personalized demonstration" />
        <link rel="canonical" href="https://www.fordje.com/request-demo" />

        <meta property="og:title" content="Book a Demo — Fordje Regulatory Data Platform" />
        <meta property="og:description" content="Book a personalized demo of Fordje. See how clean energy teams navigate codes, permitting, and interconnection across U.S. jurisdictions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fordje.com/request-demo" />
        <meta property="og:image" content="https://www.fordje.com/og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Book a Demo — Fordje Regulatory Data Platform" />
        <meta name="twitter:description" content="Book a personalized demo of Fordje. See how clean energy teams navigate codes, permitting, and interconnection across U.S. jurisdictions." />
        <meta name="twitter:image" content="https://www.fordje.com/og-image.png" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Header variant="light" />

      <main className="overflow-hidden">
        {/* Hero + Form Section */}
        <section className="relative bg-fordje-navy-dark pt-28 pb-16 lg:pb-24">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-fordje-blue/10 rounded-full blur-3xl" />
          </div>

          <div className="container-custom relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-6xl mx-auto">
              {/* Left: Value Prop */}
              <div className="animate-fade-in text-center lg:text-left">
                <h1 className="text-white mb-6 text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                  See how Fordje works for your projects
                </h1>

                <p className="text-lg md:text-xl text-fordje-gray mb-8 max-w-xl mx-auto lg:mx-0">
                  Get a personalized walkthrough of the regulatory data that speeds up your clean energy deployments — from code cycles and permitting requirements to interconnection and licensing.
                </p>

                <ul className="space-y-4 mb-8 max-w-md mx-auto lg:mx-0">
                  {[
                    'Data across 28,000+ U.S. jurisdictions and 3,400 utilites',
                    'Live look at your target AHJs and project types',
                    'Automatic alerts when requirements change'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-fordje-gray">
                      <CheckCircle2 className="h-5 w-5 text-fordje-blue mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right: Form Card */}
              <div className="bg-white p-8 md:p-10 rounded-xl shadow-2xl shadow-fordje-navy/30 animate-fade-in-right border border-fordje-gray">
                <h2 className="text-2xl md:text-3xl font-semibold mb-2 text-fordje-navy">Book your demo</h2>
                <p className="text-fordje-navy-light mb-8">Tell us a bit about you and we'll schedule a time.</p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-fordje-navy">Full Name</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        placeholder="Your name"
                        className="bg-white border-fordje-gray text-fordje-navy focus:border-fordje-blue focus:ring-fordje-blue"
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-fordje-navy">Work Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        placeholder="your.email@company.com"
                        className="bg-white border-fordje-gray text-fordje-navy focus:border-fordje-blue focus:ring-fordje-blue"
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-fordje-navy">Company</Label>
                    <Input
                      id="company"
                      value={company}
                      onChange={e => setCompany(e.target.value)}
                      required
                      placeholder="Your company"
                      className="bg-white border-fordje-gray text-fordje-navy focus:border-fordje-blue focus:ring-fordje-blue"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-fordje-navy">What are you hoping to see? (Optional)</Label>
                    <Textarea
                      id="message"
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder="e.g., interconnection rules for PG&E territory, AHJ contacts in Texas, storage fire codes..."
                      className="bg-white border-fordje-gray text-fordje-navy focus:border-fordje-blue focus:ring-fordje-blue min-h-[100px]"
                      maxLength={500}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-fordje-blue hover:bg-fordje-blue-dark text-white py-6 h-auto text-lg shadow-lg shadow-fordje-blue/20 transition-all"
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Book a Demo'}
                  </Button>

                  <p className="text-sm text-fordje-navy-light text-center flex items-center justify-center gap-2">
                    <Shield className="h-4 w-4" />
                    We respect your privacy and will never share your information.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        <CustomerLogos />

        {/* What happens next */}
        <section className="py-16 md:py-20 bg-fordje-gray-light">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-center mb-12 text-fordje-navy">What to expect</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    icon: Send,
                    title: 'Send your request',
                    description: "Tell us about your projects. We'll email back within one business day."
                  },
                  {
                    icon: MessageSquare,
                    title: 'Talk through the problem',
                    description: 'Thirty minutes on your problems, not our product — where you build, where deals stall, what your team chases.'
                  },
                  {
                    icon: CheckCircle2,
                    title: 'Leave with a plan',
                    description: 'Next steps, plus a follow-up built around your jurisdictions and project types.'
                  }
                ].map((step, i) => (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-fordje-blue/10 flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-6 w-6 text-fordje-blue" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2 text-fordje-navy">{step.title}</h3>
                    <p className="text-fordje-navy-light">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RequestDemo;
